import { Box, Stack } from '@ff-it/ui';
import type { InlineItem } from 'components/form/types';
import type { Position } from 'modules/campaign/row';
import { ReactElement, useCallback, useState } from 'react';
import { useFieldArray } from 'react-final-form-arrays';
import { quantityField, periodField, amountField } from './style.css';
import { PlanResolution } from 'modules/campaign/plan/types';
import { collectItems, splitGap } from './helpers';
import { PositionRow } from './PositionRow';
import { PlanMutations } from 'modules/campaign/plan/state/mutate';
import { useBreakdown } from './useBreakdown';
import { Breakdown } from './Breakdown';
import { useForm } from 'react-final-form';
import { SeekButton } from './SeekButton';
import { GapRow } from './GapRow';
import type { Interval } from 'core/types';

type PositionDialogProps = {
  rowId: number;
  requestHandler: PlanMutations['requestHandler'];
  interval: Interval;
  resolution: PlanResolution;
  initialActiveKey?: string | undefined;
};

export function PositionDialog({
  rowId,
  requestHandler,
  interval,
  initialActiveKey,
}: PositionDialogProps): ReactElement {
  const form = useForm();
  const [activeKey, setActiveKey] = useState<string | null>(initialActiveKey || null);
  const { fields } = useFieldArray<InlineItem<Position>>('positions', {
    subscription: { value: true },
  });

  const seekBudgetQuantity = useCallback(
    async (positionIndex: number, value: { budget: string }) => {
      const { date_from, date_to } = form.getFieldState(`positions`)!.value[positionIndex];

      const resp = await requestHandler<{ quantity: string }>({
        method: 'POST',
        url: `plan/rows/${rowId}/position_quantity_goal/`,
        body: { ...value, date_from, date_to },
      });
      form.change(`positions[${positionIndex}].quantity`, resp.quantity);
    },
    [rowId, requestHandler, form],
  );

  const breakdown = useBreakdown({ positions: fields.value, rowId, requestHandler });

  const items = collectItems(fields, interval);

  return (
    <Stack direction="vertical" space="0.5">
      <Stack direction="horizontal" px="2">
        <Box
          className={periodField}
          textSize="xs"
          color="gray500"
          textTransform="uppercase"
          display="flex"
          alignItems="center"
        >
          Period
        </Box>
        <Box
          className={quantityField}
          textSize="xs"
          color="gray500"
          textTransform="uppercase"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          Quantity
          {fields.value?.length > 1 && (
            <SeekButton
              seekBudgetQuantity={async (value) => {
                const positions = (fields.value || []).map(({ date_from, date_to }: InlineItem<Position>) => ({
                  date_from,
                  date_to,
                }));
                const resp = await requestHandler<{ positions: { quantity: string }[] }>({
                  method: 'POST',
                  url: `plan/rows/${rowId}/position_quantity_goal_bulk/`,
                  body: { ...value, positions },
                });
                form.batch(() => {
                  resp.positions.forEach(({ quantity }, positionIndex) => {
                    form.change(`positions[${positionIndex}].quantity`, quantity);
                  });
                });
              }}
            />
          )}
        </Box>
        <Box className={amountField} textSize="xs" color="gray500" textTransform="uppercase" textAlign="right">
          {breakdown && <Breakdown {...breakdown.total} />}
        </Box>
      </Stack>

      {items.map((item) => {
        switch (item.type) {
          case 'position':
            return (
              <PositionRow
                key={item.key_}
                active={item.key_ === activeKey}
                name={item.name}
                index={item.index}
                remove={fields.remove}
                minDate={item.minDate}
                maxDate={item.maxDate}
                breakdown={breakdown?.positions[item.index]}
                seekBudgetQuantity={seekBudgetQuantity}
              />
            );
          case 'gap':
            return (
              <GapRow
                key={item.key_}
                interval={item.interval}
                onInsert={(split) => {
                  const baseTime = Date.now();
                  const positions = splitGap(item.interval, split);
                  form.batch(() => {
                    positions.forEach(({ date_from, date_to }, i) => {
                      fields.insert(item.formIndex + i, {
                        date_from,
                        date_to,
                        quantity: null,
                        __NEW__: baseTime + i,
                      } as any);
                    });
                  });
                  setActiveKey(`position-new-${baseTime}`);
                }}
              />
            );
          default: {
            const _: never = item;
            throw new Error(`Unhandled item type: ${(_ as any).type}`);
          }
        }
      })}
    </Stack>
  );
}
